import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaMapMarkerAlt, FaPhoneAlt, FaExclamationTriangle, FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isImportantInfoOpen, setIsImportantInfoOpen] = useState(false);

  const handleAnchorClick = (e, anchor) => {
    e.preventDefault();
    const element = document.getElementById(anchor.replace('#', ''));
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const toggleImportantInfo = () => {
    setIsImportantInfoOpen(!isImportantInfoOpen);
  };
  
  return (
    <footer className="bg-primary text-white">
      <div className="container-custom py-12">
        
        {/* Main Footer - Strict Left Alignment */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-8 text-left">
          
          {/* Company Info - Column 1 */}
          <div>
            <div className="mb-6">
              <Link to="/" className="inline-block mb-6">
                <img 
                  src="/assets/logos/SEGWAY.png" 
                  alt="Segway Capital" 
                  className="h-10 w-auto"
                />
              </Link>
            </div>
            
            <div className="mb-6 text-left">
              <div className="mb-3">
                <div className="flex items-start">
                  <FaExclamationTriangle className="text-yellow-400 mr-2 mt-1 text-sm" />
                  <h4 className="text-sm font-semibold text-white">Risk Disclosure</h4>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed text-left">
                An investment in the funds managed by SEGWAY is speculative and involves a high degree of risk, including the risk that these funds could lose value. SEGWAY will employ certain trading techniques which may increase the risk of investment loss.
              </p>
            </div>
          </div>
          
          {/* UK Office - Column 2 */}
          <div className="text-left">
            <h4 className="text-lg font-semibold text-white mb-4 text-left">UK Office</h4>
            <div className="space-y-3">
              <div className="text-left">
                <div className="flex items-start">
                  <FaMapMarkerAlt className="text-white mt-1 mr-2 text-sm" />
                  <div className="text-sm text-gray-300 leading-relaxed text-left">
                    <div>Colmore Building</div>
                    <div>20 Colmore Circus Queensway</div>
                    <div>Birmingham, England</div>
                    <div>B4 6AT</div>
                  </div>
                </div>
              </div>
              <div className="text-left">
                <div className="flex items-start">
                  <FaPhoneAlt className="text-white mt-1 mr-2 text-sm" />
                  <span className="text-sm text-gray-300 text-left">+44 7468 903587</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Cayman Office - Column 3 */}
          <div className="text-left">
            <h4 className="text-lg font-semibold text-white mb-4 text-left">Cayman Office</h4>
            <div className="space-y-3">
              <div className="text-left">
                <div className="flex items-start">
                  <FaMapMarkerAlt className="text-white mt-1 mr-2 text-sm" />
                  <div className="text-sm text-gray-300 leading-relaxed text-left">
                    <div>PO Box 1093</div>
                    <div>Boundary Hall</div>
                    <div>Cricket Square</div>
                    <div>Grand Cayman</div>
                    <div>KY1-1102, Cayman Islands</div>
                  </div>
                </div>
              </div>
              <div className="text-left">
                <div className="flex items-start">
                  <FaEnvelope className="text-white mt-1 mr-2 text-sm" />
                  <span className="text-sm text-gray-300 text-left">info@segwaycapital.com</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Legal & Documents - Column 4 */}
          <div className="text-left">
            <h4 className="text-lg font-semibold text-white mb-4 text-left">Legal & Documents</h4>
            
            <div className="mb-6 text-left">
              <h5 className="text-sm font-semibold text-white mb-3 text-left">Quick Links</h5>
              <ul className="space-y-2 text-left">
                <li>
                  <Link to="/privacy-policy" className="text-sm text-gray-300 hover:text-white transition-colors duration-200 text-left block">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="text-sm text-gray-300 hover:text-white transition-colors duration-200 text-left block">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <button 
                    onClick={toggleImportantInfo}
                    className="text-sm text-gray-300 hover:text-white transition-colors duration-200 cursor-pointer text-left block"
                  >
                    Important Information
                  </button>
                </li>
              </ul>
            </div>
            
            <div className="text-left">
              <h5 className="text-sm font-semibold text-white mb-3 text-left">Official Documents</h5>
              <ul className="space-y-2 text-left">
                <li>
                  <a 
                    href="https://find-and-update.company-information.service.gov.uk/company/15237342"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-300 hover:text-white transition-colors duration-200 text-left block"
                  >
                    Certificate of Incorporation 2023
                  </a>
                </li>
                <li>
                  <a 
                    href="https://find-and-update.company-information.service.gov.uk/company/16496101"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-300 hover:text-white transition-colors duration-200 text-left block"
                  >
                    Certificate of Incorporation 2025
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
        </div>
        
        {/* Collapsible Important Investment Information Section */}
        <div className="border-t border-gray-700 pt-6 text-left">
          <button
            onClick={toggleImportantInfo}
            className="flex items-center justify-between w-full text-left hover:text-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50 rounded p-2"
            id="risk-warning"
          >
            <div className="flex items-center">
              <FaInfoCircle className="text-yellow-400 mr-3 text-lg" />
              <h4 className="text-lg font-semibold text-white text-left">Important Investment Information</h4>
            </div>
            <div className="flex items-center ml-4">
              {isImportantInfoOpen ? (
                <FaChevronUp className="text-gray-400 text-sm" />
              ) : (
                <FaChevronDown className="text-gray-400 text-sm" />
              )}
            </div>
          </button>
          
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isImportantInfoOpen ? 'max-h-screen md:max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}>
            <div className="text-left pl-4 md:pl-8">
              <div className="space-y-4 text-left">
                <p className="text-sm md:text-sm text-gray-300 leading-relaxed text-left">
                  Please consider your objectives before investing. Past performance is not indicative of future results. Forward-looking statements, hypothetical information or calculations, financial estimates, assumptions, or estimates are inherently uncertain. Such information should not be used as a basis for an investor's decision to invest. Securities offered by Segway Securities, LLC on the Platform, including investments in the Financial market, are speculative, highly illiquid, and involve substantial risk. You should not invest unless you can sustain the risk of loss of capital, including the risk of total loss of capital.
                </p>
                <p className="text-sm md:text-sm text-gray-300 leading-relaxed text-left">
                  <span className="text-white font-semibold">S Capital Fund, (Cayman) Limited</span> is authorised to conduct Securities Investment Business by the Cayman Islands Monetary Authority (CIMA), license number <span className="text-white font-semibold">1609101</span>. S Capital (Cayman) Limited is also registered as a Broker Member of the Cayman Islands Stock Exchange. The information contained on this site, including attachments, is not to be construed as advice unless specifically referred to as Advice.
                </p>
              </div>
            </div>
          </div>
        </div>
        
      </div>
      
      {/* Bottom Bar */}
      <div className="bg-gray-900 py-4 border-t border-gray-700">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-400 space-y-2 sm:space-y-0">
            <div className="text-left">
              &copy; {currentYear} Segway Capital. All rights reserved.
            </div>
            <div className="flex flex-row gap-4 justify-center">
              <Link 
                to="/privacy-policy" 
                className="hover:text-white transition-colors duration-200"
              >
                Privacy
              </Link>
              <Link 
                to="/terms-of-service" 
                className="hover:text-white transition-colors duration-200"
              >
                Terms
              </Link>
              <button 
                onClick={toggleImportantInfo}
                className="hover:text-white transition-colors duration-200 cursor-pointer"
              >
                Important Info
              </button>
            </div>
          </div>
        </div>
      </div>
      
    </footer>
  );
};

export default Footer; 